// src/components/MessageBubble.tsx
import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';
import { extractCodeFiles, getActiveGeneration, PreviewFile } from '../utils/extractFiles';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  streaming?: boolean;
}

interface Props {
  msg: ChatMessage;
  isUser: boolean;
  avatarUrl?: string;
  userInitial?: string;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (v: string) => void;
  onStartEdit: () => void;
  onSubmitEdit: () => void;
  onCancelEdit: () => void;
  canEdit: boolean;
  onPreview: (files: PreviewFile[]) => void;
  isLastMessage?: boolean;
  onRegenerate?: () => void;
  canRegenerate?: boolean;
}

// Parses "language:filename" out of a markdown fence's info string, e.g.
// ```html:index.html -> { language: 'html', filename: 'index.html' }
function parseInfoString(className?: string): { language: string; filename?: string } {
  const match = /language-([a-zA-Z0-9_+-]+)(?::([^\s`]+))?/.exec(className || '');
  return { language: match ? match[1] : 'text', filename: match?.[2] };
}

// Minimum length before offering a ".md" download — short answers don't
// need a download button cluttering the bubble.
const MD_DOWNLOAD_THRESHOLD = 300;

// Derives a reasonable filename from the response's own first line (e.g.
// its markdown heading), falling back to a generic name if that's empty
// or not usable as a filename.
function deriveMdFilename(content: string): string {
  const firstLine = content.split('\n')[0].replace(/^#+\s*/, '').trim();
  const slug = firstLine
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return (slug || 'response') + '.md';
}

function downloadAsMarkdown(content: string) {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = deriveMdFilename(content);
  a.click();
  URL.revokeObjectURL(url);
}

function MessageBubbleInner({
  msg, isUser, avatarUrl, userInitial, isEditing, editText, onEditTextChange,
  onStartEdit, onSubmitEdit, onCancelEdit, canEdit, onPreview,
  isLastMessage, onRegenerate, canRegenerate
}: Props) {
  const files = useMemo(() => extractCodeFiles(msg.content), [msg.content]);
  const generating = msg.streaming ? getActiveGeneration(msg.content) : null;

  return (
    <div className={`message ${msg.role}`}>
      <div className="message-avatar">
        {isUser
          ? (avatarUrl
            ? <img src={avatarUrl} alt="you" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
            : userInitial)
          : '⚡'}
      </div>
      <div className="message-content">
        {isEditing ? (
          <div className="edit-area">
            <textarea
              className="edit-textarea"
              value={editText}
              onChange={e => onEditTextChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmitEdit(); }
                if (e.key === 'Escape') onCancelEdit();
              }}
              autoFocus
              rows={3}
            />
            <div className="edit-actions">
              <button className="edit-save-btn" onClick={onSubmitEdit}>Send ➤</button>
              <button className="edit-cancel-btn" onClick={onCancelEdit}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, className, children, inline, ...props }: any) {
                  const isBlock = !inline;
                  const codeStr = String(children).replace(/\n$/, '');
                  if (!isBlock) {
                    return <code className={className} {...props}>{children}</code>;
                  }
                  const { language, filename } = parseInfoString(className);
                  return (
                    <CodeBlock
                      language={language}
                      filename={filename}
                      code={codeStr}
                      onPreview={(code, lang, fname) => onPreview([{
                        filename: fname || `snippet.${lang || 'txt'}`,
                        language: lang,
                        code,
                      }])}
                    />
                  );
                }
              }}
            >
              {msg.content}
            </ReactMarkdown>

            {generating && (
              <div className="gen-status" role="status">
                <span className="gen-spinner" />
                <span className="gen-status-text">
                  Writing {generating.filename || (generating.language ? `${generating.language} code` : 'code')}
                  {generating.lineCount > 0 && <> — {generating.lineCount} lines…</>}
                </span>
              </div>
            )}

            {!msg.streaming && files.length > 1 && (
              <div className="files-toolbar">
                <span className="files-toolbar-label">🗂 {files.length} files generated</span>
                <button className="files-toolbar-btn" onClick={() => onPreview(files)}>
                  Open all in Preview
                </button>
              </div>
            )}

            {msg.streaming && <span className="cursor" />}
            {msg.role === 'user' && canEdit && (
              <button className="msg-edit-btn" onClick={onStartEdit} title="Edit message">✏️</button>
            )}
            {msg.role === 'assistant' && !msg.streaming && isLastMessage && onRegenerate && (
              <button
                className="msg-regenerate-btn"
                onClick={onRegenerate}
                disabled={!canRegenerate}
                title="Regenerate this response"
              >
                🔄 Regenerate
              </button>
            )}
            {msg.role === 'assistant' && !msg.streaming && msg.content.trim().length > MD_DOWNLOAD_THRESHOLD && (
              <button
                className="msg-download-md-btn"
                onClick={() => downloadAsMarkdown(msg.content)}
                title="Download this response as a .md file"
              >
                ⬇ .md
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Only re-render a bubble when ITS OWN content/streaming/editing state changes —
// not when a sibling message or the global chatLoading flag changes.
// isLastMessage/canRegenerate ARE checked (unlike most global flags) because
// appending a new message flips the previous assistant bubble's
// isLastMessage from true to false, and the Regenerate button must
// disappear from it at that point — skipping this check would leave a
// stale Regenerate button visible on a message that's no longer last.
export default memo(MessageBubbleInner, (prev, next) =>
  prev.msg.content === next.msg.content &&
  prev.msg.streaming === next.msg.streaming &&
  prev.isEditing === next.isEditing &&
  prev.editText === next.editText &&
  prev.canEdit === next.canEdit &&
  prev.avatarUrl === next.avatarUrl &&
  prev.isLastMessage === next.isLastMessage &&
  prev.canRegenerate === next.canRegenerate
);